import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Trash, DotsSixVertical, Sparkle, TextAa, Newspaper, LineSegment, FileText,
} from "@phosphor-icons/react";

const SECTION_ICONS = {
  cover: Newspaper,
  strategy_note: Sparkle,
  content_item: FileText,
  custom_text: TextAa,
  divider: LineSegment,
};

const ADD_MENU_OPTIONS = [
  { type: "strategy_note", label: "Strategy Note", icon: Sparkle },
  { type: "content_item", label: "Content Item", icon: FileText, picker: true },
  { type: "custom_text", label: "Custom Text", icon: TextAa },
  { type: "divider", label: "Divider", icon: LineSegment },
];

export function SectionManager({
  sections, selectedId, onSelectSection, onDeleteSection,
  onDragStart, onDragOver, onDrop, addMenuOpen, onToggleAddMenu,
  onAddSection, onOpenContentPicker,
}) {
  return (
    <div className="w-[280px] shrink-0 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg overflow-hidden flex flex-col" data-testid="section-manager">
      <div className="p-3 border-b border-[var(--border)] flex items-center justify-between">
        <span className="text-xs font-medium text-[var(--text-secondary)]">SECTIONS</span>
        <span className="text-[10px] font-['JetBrains_Mono'] text-[var(--text-muted)]">{sections.length}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {sections.map((sec, idx) => {
          const Icon = SECTION_ICONS[sec.section_type] || FileText;
          const isSelected = sec.section_id === selectedId;
          return (
            <div
              key={sec.section_id}
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(idx)}
              onClick={() => onSelectSection(sec.section_id)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-md cursor-pointer group transition-all text-sm ${
                isSelected ? "bg-[#FF4400]/10 border border-[#FF4400]/20 text-[#FF4400]" : "hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]"
              }`}
              data-testid={`section-item-${sec.section_id}`}
            >
              <DotsSixVertical size={14} className="text-[var(--text-muted)] opacity-0 group-hover:opacity-100 cursor-grab shrink-0" />
              <Icon size={14} className="shrink-0" />
              <span className="flex-1 truncate text-xs">{sec.title || sec.section_type.replace("_", " ")}</span>
              {sec.section_type !== "cover" && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteSection(sec.section_id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded text-[var(--text-muted)] hover:text-red-400 transition-all shrink-0"
                  data-testid={`delete-section-${sec.section_id}`}
                >
                  <Trash size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="p-2 border-t border-[var(--border)] relative">
        <button
          onClick={onToggleAddMenu}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium bg-[var(--bg-base)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[#FF4400] hover:border-[#FF4400]/30 transition-colors"
          data-testid="add-section-btn"
        >
          <Plus size={14} /> Add Section
        </button>
        <AnimatePresence>
          {addMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              className="absolute bottom-full left-2 right-2 mb-1 bg-[var(--bg-raised)] border border-[var(--border-strong)] rounded-lg shadow-lg overflow-hidden z-10"
              data-testid="add-section-menu"
            >
              {ADD_MENU_OPTIONS.map((opt) => (
                <button
                  key={opt.type}
                  onClick={() => opt.picker ? onOpenContentPicker() : onAddSection(opt.type)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] transition-colors"
                  data-testid={`add-${opt.type}`}
                >
                  <opt.icon size={14} /> {opt.label}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
